import Head from 'next/head';
import React from 'react';
import { gql } from '@apollo/client';
import type { GetStaticPaths, GetStaticProps } from 'next';

import Layout from '../../../components/layout';
import ArticleResultsList from '../../../components/article/results-list';
import { ListingHeader } from '../../../components/article/ListingHeader';
import {
  client,
  getTeamRevealsByAuthorNameQuery
} from '../../../apollo-client';
import { TEAM_REVEALS_SECTION_LABEL } from '@lib/article-seo';
import { FFH_HOSTNAME, SITE_NAME } from '@lib/constants';
import { resolveWordPressAuthorHandle } from '@lib/listings/legacy-handles';
import { normaliseSanityListingItem } from '@lib/listings/normalise';
import type { WordPressPostsData } from '@lib/listings/normalise';
import { toPageProps } from '@lib/listings/page-props';
import type { ListingItem } from '@lib/listings/types';
import {
  buildSanityImageUrl,
  getAuthor,
  getTeamRevealCardsByAuthor,
  TEAM_REVEALS_PATH_PREFIX
} from '@lib/sanity';
import type { SanityAuthor } from '@lib/sanity';

export const REVALIDATE_SECONDS = 600;
export const NOT_FOUND_REVALIDATE_SECONDS = 600;
const AVATAR_WIDTH = 160;

export type TeamRevealListingProps = {
  articlesList: WordPressPostsData | null;
  author: SanityAuthor | null;
  sanityItems: ListingItem[];
  authorSlug: string;
};

/**
 * Per-contributor listing of team reveals: Sanity reveals at
 * /team-reveals/<author>/<slug> (FFH-2870) interleaved with the WordPress
 * reveals still filed under the contributor's old login.
 */
const TeamRevealListing = ({
  articlesList,
  author,
  sanityItems,
  authorSlug
}: TeamRevealListingProps) => {
  const wordPressAuthorName =
    articlesList?.posts?.edges?.[0]?.node.author?.node?.name;
  const name = author?.name ?? wordPressAuthorName ?? authorSlug;
  const description = author?.teamRevealBio ?? author?.bio;
  const canonicalUrl = `${FFH_HOSTNAME}${TEAM_REVEALS_PATH_PREFIX}/${authorSlug}`;
  const avatarUrl = author?.avatar?.asset
    ? buildSanityImageUrl(author.avatar)
        .width(AVATAR_WIDTH)
        .height(AVATAR_WIDTH)
        .url()
    : undefined;

  return (
    <div>
      <Head>
        <title>{`${name} ${TEAM_REVEALS_SECTION_LABEL.toLowerCase()} | ${SITE_NAME}`}</title>
        {description && <meta name="description" content={description} />}
        <link rel="canonical" href={canonicalUrl} key="canonical" />
      </Head>

      <Layout>
        <div className="relative bg-gray-50 pt-10 pb-20 px-4 sm:px-6 lg:pt-14 lg:pb-10 lg:px-8">
          <div className="absolute">
            <div className="bg-white h-1/3 sm:h-2/3" />
          </div>
          <ListingHeader
            title={name}
            eyebrow={TEAM_REVEALS_SECTION_LABEL}
            description={description}
            avatarUrl={avatarUrl}
          />
          <ArticleResultsList
            initialData={articlesList ?? undefined}
            category={{ name: authorSlug }}
            query={getTeamRevealsByAuthorNameQuery(
              resolveWordPressAuthorHandle(authorSlug)
            )}
            sanityItems={sanityItems}
            type="categories"
          />
        </div>
      </Layout>
    </div>
  );
};

export const getStaticProps: GetStaticProps<TeamRevealListingProps> = async ({
  params
}) => {
  const authorSlug = params?.author as string;
  const wordPressHandle = resolveWordPressAuthorHandle(authorSlug);

  try {
    const [{ data }, author, sanityCards] = await Promise.all([
      client.query({
        query: gql`
          ${getTeamRevealsByAuthorNameQuery(wordPressHandle)}
        `,
        fetchPolicy: 'network-only'
      }),
      getAuthor(authorSlug),
      getTeamRevealCardsByAuthor(authorSlug)
    ]);

    const hasWordPressReveals = (data?.posts?.edges?.length ?? 0) > 0;
    if (!author && !hasWordPressReveals) {
      return { notFound: true, revalidate: NOT_FOUND_REVALIDATE_SECONDS };
    }

    return {
      props: {
        articlesList: data,
        author,
        sanityItems: toPageProps(sanityCards.map(normaliseSanityListingItem)),
        authorSlug
      },
      revalidate: REVALIDATE_SECONDS
    };
  } catch (error) {
    console.error(
      `Failed to fetch team reveals for author "${authorSlug}":`,
      error
    );
    throw error;
  }
};

export const getStaticPaths: GetStaticPaths = async () => ({
  paths: [],
  fallback: 'blocking'
});

export default TeamRevealListing;
